import { useState } from 'react';
import { toast } from 'react-toastify';
import { NotificationsActive } from '@mui/icons-material';
import { Button } from '@components/atoms/Button';
import { ConfirmationModal } from '@components/molecules/ConfirmationModal';
import {
  useCreateSuperAdminNotificationRecipient,
  useDeleteSuperAdminNotificationRecipient,
  useSuperAdminNotificationRecipients,
  useUpdateSuperAdminNotificationRecipient,
} from '@hooks/api/super-admin-notification-recipients';
import {
  SuperAdminNotificationRecipient,
  SuperAdminNotificationRecipientCreateInput,
  SuperAdminNotificationRecipientUpdateInput,
} from '@models/superAdminNotificationRecipient.model';
import CreateEditRecipientModal from './components/CreateEditRecipientModal';
import SuperAdminNotificationRecipientsList from './list';
import { useTranslation } from '@hooks/useTranslation';

const extractErrorMessage = (err: any, fallback: string): string =>
  err?.response?.errors?.[0]?.message || err?.message || fallback;

const SuperAdminNotificationRecipientsPage: React.FC = () => {
  const tActionMessages = useTranslation('actionMessages');
  const { data: recipients = [], isLoading } = useSuperAdminNotificationRecipients();
  const createMutation = useCreateSuperAdminNotificationRecipient();
  const updateMutation = useUpdateSuperAdminNotificationRecipient();
  const deleteMutation = useDeleteSuperAdminNotificationRecipient();

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editing, setEditing] = useState<SuperAdminNotificationRecipient | null>(null);

  const [recipientToDelete, setRecipientToDelete] =
    useState<SuperAdminNotificationRecipient | null>(null);
  const [togglingId, setTogglingId] = useState<string | null>(null);

  const handleOpenCreate = () => {
    setEditing(null);
    setIsFormOpen(true);
  };

  const handleOpenEdit = (recipient: SuperAdminNotificationRecipient) => {
    setEditing(recipient);
    setIsFormOpen(true);
  };

  const handleCloseForm = () => {
    setIsFormOpen(false);
    setEditing(null);
  };

  const handleCreate = async (input: SuperAdminNotificationRecipientCreateInput) => {
    try {
      await createMutation.mutateAsync(input);
      toast.success(tActionMessages.recipientAdded);
      handleCloseForm();
    } catch (err) {
      toast.error(extractErrorMessage(err, 'Failed to add recipient'));
    }
  };

  const handleUpdate = async (input: SuperAdminNotificationRecipientUpdateInput) => {
    try {
      await updateMutation.mutateAsync(input);
      toast.success(tActionMessages.recipientUpdated);
      handleCloseForm();
    } catch (err) {
      toast.error(extractErrorMessage(err, 'Failed to update recipient'));
    }
  };

  const handleToggleActive = async (recipient: SuperAdminNotificationRecipient) => {
    setTogglingId(recipient.id);
    try {
      await updateMutation.mutateAsync({ id: recipient.id, active: !recipient.active });
      toast.success(recipient.active ? tActionMessages.recipientDisabled : tActionMessages.recipientEnabled);
    } catch (err) {
      toast.error(extractErrorMessage(err, 'Failed to update recipient'));
    } finally {
      setTogglingId(null);
    }
  };

  const handleConfirmDelete = async () => {
    if (!recipientToDelete) return;
    try {
      await deleteMutation.mutateAsync(recipientToDelete.id);
      toast.success(tActionMessages.recipientDeleted);
    } catch (err) {
      toast.error(extractErrorMessage(err, 'Failed to delete recipient'));
    } finally {
      setRecipientToDelete(null);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2">
          <NotificationsActive
            sx={{ fontSize: 28 }}
            className="text-indigo-500 dark:text-indigo-300"
          />
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            Super Admin Notifications
          </h1>
        </div>
        <Button variant="primary" onClick={handleOpenCreate}>
          Add Recipient
        </Button>
      </div>

      <p className="text-sm text-gray-600 dark:text-gray-300 mb-6 max-w-3xl">
        These emails receive every super-admin notification (for example, when a new organization
        owner signs up). Disable a recipient to stop sending without losing the entry.
      </p>

      <SuperAdminNotificationRecipientsList
        recipients={recipients}
        isLoading={isLoading}
        togglingId={togglingId}
        onEdit={handleOpenEdit}
        onDelete={(r) => setRecipientToDelete(r)}
        onToggleActive={handleToggleActive}
      />

      <CreateEditRecipientModal
        isOpen={isFormOpen}
        onClose={handleCloseForm}
        onCreate={handleCreate}
        onUpdate={handleUpdate}
        saving={createMutation.isPending || updateMutation.isPending}
        editing={!!editing}
        initialData={editing}
      />

      <ConfirmationModal
        isOpen={!!recipientToDelete}
        onClose={() => setRecipientToDelete(null)}
        onConfirm={handleConfirmDelete}
        title="Delete notification recipient"
        description={
          recipientToDelete
            ? `Are you sure you want to delete ${recipientToDelete.email}? This action cannot be undone.`
            : ''
        }
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
      />
    </div>
  );
};

export default SuperAdminNotificationRecipientsPage;
